import type { Translated } from "@repo/shared/types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/hooks/use-confirm";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { ClientAccountWithPositions } from "@/lib/types/client-account";
import { cn } from "@/lib/utils";
import { T } from "./accounts-section.i18n";
import { FollowupSelectionDialog } from "./followup-selection-dialog";
import { formatUnits, formatWholeAmount } from "./order-product-name";
import { TotalValueCard } from "./total-value-card";

// Accounts are read-only here: they are created by flow completion or by the
// bank via the public API, and only the public API may modify them.

type AccountsT = Translated<typeof T>;

type AccountsSectionProps = {
  clientId: string;
  accounts: ClientAccountWithPositions[];
};

// Private helper — labeled read-only details grid shown in the expanded panel.
function AccountDetails({ account, t }: { account: ClientAccountWithPositions; t: AccountsT }) {
  const { formatNumber } = useRegionalFormat();
  const fields: Array<{ label: string; value: string; mono?: boolean }> = [
    { label: t.externalId, value: account.externalId || t.notSet, mono: !!account.externalId },
    { label: t.vendor, value: account.vendor || t.notSet },
    { label: t.currency, value: account.currency || t.notSet },
    {
      label: t.cashAmount,
      value: formatCurrency(account.cashAmount, account.currency, formatNumber),
    },
    { label: t.wrapperType, value: account.wrapperType || t.notSet },
    { label: t.linkedGoal, value: account.goal?.name || t.notSet },
  ];

  // Two label/value pairs per striped row.
  const rows: (typeof fields)[] = [];
  for (let i = 0; i < fields.length; i += 2) rows.push(fields.slice(i, i + 2));

  return (
    <div className="p-3 border-b text-sm">
      <div className="rounded-md overflow-hidden">
        {rows.map((row, i) => (
          <div
            key={row[0]?.label}
            className={cn(
              "grid grid-cols-[8rem_1fr] md:grid-cols-[8rem_1fr_8rem_1fr] gap-x-4 px-4 py-2",
              i % 2 === 0 && "bg-muted/30",
            )}
          >
            {row.map((f) => (
              <div key={f.label} className="contents">
                <div className="text-muted-foreground">{f.label}</div>
                <div className={cn(f.mono && "font-mono")}>{f.value}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function formatCurrency(
  value: number,
  currency: string | null | undefined,
  formatNumber: (v: number, opts?: { maximumFractionDigits?: number }) => string,
): string {
  const formatted = formatWholeAmount(value, formatNumber);
  // Mirrors the previous Intl currencyDisplay:"code" output: code before number.
  return currency ? `${currency} ${formatted}` : formatted;
}

export function AccountsSection({ clientId, accounts }: AccountsSectionProps) {
  const { formatNumber } = useRegionalFormat();
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
  const { confirm, ConfirmationDialog } = useConfirm();
  const [followupAccount, setFollowupAccount] = useState<ClientAccountWithPositions | null>(null);

  const toggleExpanded = (accountId: string) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  };

  const showFollowupError = (message: string) => {
    void confirm({
      title: t.error,
      description: message,
      confirmText: t.ok,
      variant: "destructive",
      type: "alert",
    });
  };

  const calculateTotalValue = (account: ClientAccountWithPositions): number => {
    const positionsTotal = account.positions.reduce((sum, p) => sum + p.amount, 0);
    return positionsTotal + (account.cashAmount || 0);
  };

  return (
    <>
      <div className="space-y-4">
        <TotalValueCard clientId={clientId} />
        {accounts.length === 0 ? (
          <EmptyState>{t.noAccountsYet}</EmptyState>
        ) : (
          <div className="space-y-2">
            {accounts.map((account) => {
              const isExpanded = expandedAccounts.has(account.id);
              const totalValue = calculateTotalValue(account);

              return (
                <div key={account.id} className="border rounded-lg">
                  {/* Account header row */}
                  {/* biome-ignore lint/a11y/useSemanticElements: div with role="button" needed due to nested interactive elements */}
                  <div
                    role="button"
                    tabIndex={0}
                    className={cn(
                      "flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50",
                      isExpanded && "border-b",
                    )}
                    onClick={() => toggleExpanded(account.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleExpanded(account.id);
                      }
                    }}
                  >
                    <button
                      type="button"
                      className="p-1 hover:bg-muted rounded"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(account.id);
                      }}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{account.name}</span>
                        {/* Defensive badge — the internal list is synced-only
                            server-side, so pending never renders. */}
                        {account.status === "pending" && (
                          <span className="text-xs text-warning bg-warning/10 px-2 py-0.5 rounded">
                            {t.pendingAccount}
                          </span>
                        )}
                        {account.vendor && (
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                            {account.vendor}
                          </span>
                        )}
                        {account.goal && (
                          <span
                            className="text-xs text-info bg-info/10 px-2 py-0.5 rounded"
                            title={account.goal.name}
                          >
                            {account.goal.name}
                          </span>
                        )}
                      </div>
                      {account.externalId && (
                        <div className="text-xs text-muted-foreground font-mono">
                          {account.externalId}
                        </div>
                      )}
                    </div>

                    {/* Fixed-width slot (always rendered) so the button and the
                        amount column line up across rows regardless of content */}
                    <div className="w-28 shrink-0 flex justify-end">
                      {account.positions.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFollowupAccount(account);
                          }}
                        >
                          {t.followup}
                        </Button>
                      )}
                    </div>

                    <div className="min-w-[8rem] shrink-0 text-right">
                      <div className="font-medium">
                        {formatCurrency(totalValue, account.currency, formatNumber)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {account.positions.length + (account.cashAmount > 0 ? 1 : 0)}{" "}
                        {account.positions.length + (account.cashAmount > 0 ? 1 : 0) !== 1
                          ? t.positions
                          : t.position}
                      </div>
                    </div>
                  </div>

                  {/* Account details grid */}
                  {isExpanded && <AccountDetails account={account} t={t} />}

                  {/* Positions table */}
                  {isExpanded && (account.positions.length > 0 || account.cashAmount > 0) && (
                    <div className="p-3 bg-muted/30">
                      <Table variant="data" surface={false}>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[150px]">{t.isin}</TableHead>
                            <TableHead>{t.name}</TableHead>
                            <TableHead className="w-[100px]">{t.type}</TableHead>
                            <TableHead className="text-right w-[150px]">{t.holding}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {account.positions.map((position) => (
                            <TableRow key={position.id}>
                              <TableCell className="font-mono text-xs">
                                {position.isin ?? "-"}
                              </TableCell>
                              <TableCell>{position.name || "-"}</TableCell>
                              <TableCell>
                                {position.securityType ? (
                                  <span className="text-xs bg-muted px-2 py-1 rounded whitespace-nowrap">
                                    {position.securityType}
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {/* The value leads. A holding with units may have been
                                    valued by QP itself (units × last price, when the bank
                                    sent units only — client-account-service), and the
                                    stored row cannot tell that case from a bank-reported
                                    amount, so every holding with units is marked as an
                                    estimate. Without units the amount is the bank's. */}
                                {formatCurrency(position.amount, account.currency, formatNumber)}
                                {position.units != null && (
                                  <span className="text-muted-foreground">
                                    {" "}
                                    ({formatUnits(position.units, userLanguage)}, {t.estimated})
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                          {account.cashAmount > 0 && (
                            <TableRow>
                              <TableCell className="font-mono text-xs">-</TableCell>
                              <TableCell>{t.cash}</TableCell>
                              <TableCell>
                                <span className="text-xs bg-muted px-2 py-1 rounded">{t.cash}</span>
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(account.cashAmount, account.currency, formatNumber)}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {isExpanded && account.positions.length === 0 && account.cashAmount === 0 && (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      {t.noPositions}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmationDialog />

      <FollowupSelectionDialog
        clientId={clientId}
        target={
          followupAccount
            ? {
                kind: "account",
                accountId: followupAccount.id,
                goalFlowInstanceId: followupAccount.goal?.flowInstanceId ?? null,
              }
            : null
        }
        onClose={() => setFollowupAccount(null)}
        onError={showFollowupError}
      />
    </>
  );
}
